import mapValues from 'lodash/mapValues.js';
import doctrine from 'doctrine';
import { combineParameters } from '@storybook/preview-api';

// src/argTypes/convert/typescript/convert.ts
var convertSig = (type) => {
  switch (type.type) {
    case "function":
      return { name: "function" };
    case "object":
      const values = {};
      type.signature.properties.forEach((prop) => {
        values[prop.key] = convert(prop.value);
      });
      return {
        name: "object",
        value: values
      };
    default:
      throw new Error(`Unknown: ${type}`);
  }
};
var convert = (type) => {
  const { name, raw } = type;
  const base = {};
  if (typeof raw !== "undefined")
    base.raw = raw;
  switch (type.name) {
    case "string":
    case "number":
    case "symbol":
    case "boolean": {
      return { ...base, name };
    }
    case "Array": {
      return { ...base, name: "array", value: type.elements.map(convert) };
    }
    case "signature":
      return { ...base, ...convertSig(type) };
    case "union":
    case "intersection":
      return { ...base, name, value: type.elements.map(convert) };
    default:
      return { ...base, name: "other", value: name };
  }
};

// src/argTypes/convert/flow/convert.ts
var isLiteral = (type) => type.name === "literal";
var toEnumOption = (element) => element.value.replace(/['|"]/g, "");
var convertSig2 = (type) => {
  switch (type.type) {
    case "function":
      return { name: "function" };
    case "object":
      const values = {};
      type.signature.properties.forEach((prop) => {
        values[prop.key] = convert2(prop.value);
      });
      return {
        name: "object",
        value: values
      };
    default:
      throw new Error(`Unknown: ${type}`);
  }
};
var convert2 = (type) => {
  const { name, raw } = type;
  const base = {};
  if (typeof raw !== "undefined")
    base.raw = raw;
  switch (type.name) {
    case "literal":
      return { ...base, name: "other", value: type.value };
    case "string":
    case "number":
    case "symbol":
    case "boolean": {
      return { ...base, name };
    }
    case "Array": {
      return { ...base, name: "array", value: type.elements.map(convert2) };
    }
    case "signature":
      return { ...base, ...convertSig2(type) };
    case "union":
      if (type.elements.every(isLiteral)) {
        return { ...base, name: "enum", value: type.elements.map(toEnumOption) };
      }
      return { ...base, name, value: type.elements.map(convert2) };
    case "intersection":
      return { ...base, name, value: type.elements.map(convert2) };
    default:
      return { ...base, name: "other", value: name };
  }
};

// src/argTypes/convert/utils.ts
var QUOTE_REGEX = /^['"]|['"]$/g;
var trimQuotes = (str2) => str2.replace(QUOTE_REGEX, "");
var includesQuotes = (str2) => QUOTE_REGEX.test(str2);

// src/argTypes/convert/proptypes/convert.ts
var SIGNATURE_REGEXP = /^\(.*\) => /;
var convert3 = (type) => {
  const { name, raw, computed, value } = type;
  const base = {};
  if (typeof raw !== "undefined")
    base.raw = raw;
  switch (name) {
    case "enum": {
      const values2 = computed ? value : value.map((v) => {
        const trimmedValue = trimQuotes(v.value);
        return includesQuotes(v.value) || Number.isNaN(Number(trimmedValue)) ? trimmedValue : Number(trimmedValue);
      });
      return { ...base, name, value: values2 };
    }
    case "string":
    case "number":
    case "symbol":
      return { ...base, name };
    case "func":
      return { ...base, name: "function" };
    case "bool":
    case "boolean":
      return { ...base, name: "boolean" };
    case "arrayOf":
    case "array":
      return { ...base, name: "array", value: value && convert3(value) };
    case "object":
      return { ...base, name };
    case "objectOf":
      return { ...base, name, value: convert3(value) };
    case "shape":
    case "exact":
      const values = mapValues(value, (field) => convert3(field));
      return { ...base, name: "object", value: values };
    case "union":
      return { ...base, name: "union", value: value.map((v) => convert3(v)) };
    case "instanceOf":
    case "element":
    case "elementType":
    default: {
      if (name?.indexOf("|") > 0) {
        try {
          const literalValues = name.split("|").map((v) => JSON.parse(v));
          return { ...base, name: "enum", value: literalValues };
        } catch (err) {
        }
      }
      const otherVal = value ? `${name}(${value})` : name;
      const otherName = SIGNATURE_REGEXP.test(name) ? "function" : "other";
      return { ...base, name: otherName, value: otherVal };
    }
  }
};

// src/argTypes/convert/index.ts
var convert4 = (docgenInfo) => {
  const { type, tsType, flowType } = docgenInfo;
  if (type != null)
    return convert3(type);
  if (tsType != null)
    return convert(tsType);
  if (flowType != null)
    return convert2(flowType);
  return null;
};

// src/argTypes/docgen/types.ts
var TypeSystem = /* @__PURE__ */ ((TypeSystem2) => {
  TypeSystem2["JAVASCRIPT"] = "JavaScript";
  TypeSystem2["FLOW"] = "Flow";
  TypeSystem2["TYPESCRIPT"] = "TypeScript";
  TypeSystem2["UNKNOWN"] = "Unknown";
  return TypeSystem2;
})(TypeSystem || {});

// src/argTypes/docgen/utils/defaultValue.ts
var BLACKLIST = ["null", "undefined"];
function isDefaultValueBlacklisted(value) {
  return BLACKLIST.some((x) => x === value);
}

// src/argTypes/docgen/utils/string.ts
var str = (obj) => {
  if (!obj) {
    return "";
  }
  if (typeof obj === "string") {
    return obj;
  }
  throw new Error(`Description: expected string, got: ${JSON.stringify(obj)}`);
};

// src/argTypes/docgen/utils/docgenInfo.ts
function hasDocgen(component) {
  return !!component.__docgenInfo;
}
function isValidDocgenSection(docgenSection) {
  return docgenSection != null && Object.keys(docgenSection).length > 0;
}
function getDocgenSection(component, section) {
  return hasDocgen(component) ? component.__docgenInfo[section] : null;
}
function getDocgenDescription(component) {
  return hasDocgen(component) && str(component.__docgenInfo.description);
}
function containsJsDoc(value) {
  return value != null && value.includes("@");
}
function parse(content, tags) {
  let ast;
  try {
    ast = doctrine.parse(content, {
      tags,
      sloppy: true
    });
  } catch (e) {
    console.error(e);
    throw new Error("Cannot parse JSDoc tags.");
  }
  return ast;
}
var DEFAULT_OPTIONS = {
  tags: ["param", "arg", "argument", "returns", "ignore", "deprecated"]
};
var parseJsDoc = (value, options = DEFAULT_OPTIONS) => {
  if (!containsJsDoc(value)) {
    return {
      includesJsDoc: false,
      ignore: false
    };
  }
  const jsDocAst = parse(value, options.tags);
  const extractedTags = extractJsDocTags(jsDocAst);
  if (extractedTags.ignore) {
    return {
      includesJsDoc: true,
      ignore: true
    };
  }
  return {
    includesJsDoc: true,
    ignore: false,
    // Always use the parsed description to ensure JSDoc is removed from the description.
    description: jsDocAst.description,
    extractedTags
  };
};
function extractJsDocTags(ast) {
  const extractedTags = {
    params: null,
    deprecated: null,
    returns: null,
    ignore: false
  };
  for (let i = 0; i < ast.tags.length; i += 1) {
    const tag = ast.tags[i];
    if (tag.title === "ignore") {
      extractedTags.ignore = true;
      break;
    } else {
      switch (tag.title) {
        case "param":
        case "arg":
        case "argument": {
          const paramTag = extractParam(tag);
          if (paramTag != null) {
            if (extractedTags.params == null) {
              extractedTags.params = [];
            }
            extractedTags.params.push(paramTag);
          }
          break;
        }
        case "deprecated": {
          const deprecatedTag = extractDeprecated(tag);
          if (deprecatedTag != null) {
            extractedTags.deprecated = deprecatedTag;
          }
          break;
        }
        case "returns": {
          const returnsTag = extractReturns(tag);
          if (returnsTag != null) {
            extractedTags.returns = returnsTag;
          }
          break;
        }
      }
    }
  }
  return extractedTags;
}
function extractParam(tag) {
  const paramName = tag.name;
  if (paramName != null && paramName !== "null-null") {
    return {
      name: tag.name,
      type: tag.type,
      description: tag.description,
      getPrettyName: () => {
        if (paramName.includes("null")) {
          return paramName.replace("-null", "").replace(".null", "");
        }
        return tag.name;
      },
      getTypeName: () => {
        return tag.type != null ? extractTypeName(tag.type) : null;
      }
    };
  }
  return null;
}
function extractDeprecated(tag) {
  if (tag.title != null) {
    return tag.description;
  }
  return null;
}
function extractReturns(tag) {
  if (tag.type != null) {
    return {
      type: tag.type,
      description: tag.description,
      getTypeName: () => {
        return extractTypeName(tag.type);
      }
    };
  }
  return null;
}
function extractTypeName(type) {
  if (type.type === "NameExpression") {
    return type.name;
  }
  if (type.type === "RecordType") {
    const recordFields = type.fields.map((field) => {
      if (field.value != null) {
        const valueTypeName = extractTypeName(field.value);
        return `${field.key}: ${valueTypeName}`;
      }
      return field.key;
    });
    return `({${recordFields.join(", ")}})`;
  }
  if (type.type === "UnionType") {
    const unionElements = type.elements.map(extractTypeName);
    return `(${unionElements.join("|")})`;
  }
  if (type.type === "ArrayType") {
    return "[]";
  }
  if (type.type === "TypeApplication") {
    if (type.expression != null) {
      if (type.expression.name === "Array") {
        const arrayType = extractTypeName(type.applications[0]);
        return `${arrayType}[]`;
      }
    }
  }
  if (type.type === "NullableType" || type.type === "NonNullableType" || type.type === "OptionalType") {
    return extractTypeName(type.expression);
  }
  if (type.type === "AllLiteral") {
    return "any";
  }
  return null;
}

// src/argTypes/utils.ts
var MAX_TYPE_SUMMARY_LENGTH = 90;
var MAX_DEFAULT_VALUE_SUMMARY_LENGTH = 50;
function isTooLongForTypeSummary(value) {
  return value.length > MAX_TYPE_SUMMARY_LENGTH;
}
function isTooLongForDefaultValueSummary(value) {
  return value.length > MAX_DEFAULT_VALUE_SUMMARY_LENGTH;
}
function createSummaryValue(summary, detail) {
  if (summary === detail) {
    return { summary };
  }
  return { summary, detail };
}
var normalizeNewlines = (string) => string.replace(/\\r\\n/g, "\\n");

// src/argTypes/docgen/flow/createType.ts
function generateUnionElement({ name, value, elements, raw }) {
  if (value != null) {
    return value;
  }
  if (elements != null) {
    return elements.map(generateUnionElement).join(" | ");
  }
  if (raw != null) {
    return raw;
  }
  return name;
}
function generateUnion({ name, raw, elements }) {
  if (elements != null) {
    return createSummaryValue(elements.map(generateUnionElement).join(" | "));
  }
  if (raw != null) {
    return createSummaryValue(raw.replace(/^\|\s*/, ""));
  }
  return createSummaryValue(name);
}
function generateFuncSignature({ type, raw }) {
  if (raw != null) {
    return createSummaryValue(raw);
  }
  return createSummaryValue(type);
}
function generateObjectSignature({ type, raw }) {
  if (raw != null) {
    return !isTooLongForTypeSummary(raw) ? createSummaryValue(raw) : createSummaryValue(type, raw);
  }
  return createSummaryValue(type);
}
function generateSignature(flowType) {
  const { type } = flowType;
  return type === "object" ? generateObjectSignature(flowType) : generateFuncSignature(flowType);
}
function generateDefault({ name, raw }) {
  if (raw != null) {
    return !isTooLongForTypeSummary(raw) ? createSummaryValue(raw) : createSummaryValue(name, raw);
  }
  return createSummaryValue(name);
}
function createType(type) {
  if (type == null) {
    return null;
  }
  switch (type.name) {
    case "union" /* UNION */:
      return generateUnion(type);
    case "signature" /* SIGNATURE */:
      return generateSignature(type);
    default:
      return generateDefault(type);
  }
}

// src/argTypes/docgen/flow/createDefaultValue.ts
function createDefaultValue(defaultValue, type) {
  if (defaultValue != null) {
    const { value } = defaultValue;
    if (!isDefaultValueBlacklisted(value)) {
      return !isTooLongForDefaultValueSummary(value) ? createSummaryValue(value) : createSummaryValue(type.name, value);
    }
  }
  return null;
}

// src/argTypes/docgen/flow/createPropDef.ts
var createFlowPropDef = (propName, docgenInfo) => {
  const { flowType, description, required, defaultValue } = docgenInfo;
  return {
    name: propName,
    type: createType(flowType),
    required,
    description,
    defaultValue: createDefaultValue(defaultValue, flowType)
  };
};

// src/argTypes/docgen/typeScript/createType.ts
function createType2({ tsType, required }) {
  if (tsType == null) {
    return null;
  }
  if (!required) {
    return createSummaryValue(tsType.name.replace(" | undefined", ""));
  }
  return createSummaryValue(tsType.name);
}

// src/argTypes/docgen/typeScript/createDefaultValue.ts
function createDefaultValue2({ defaultValue }) {
  if (defaultValue != null) {
    const { value } = defaultValue;
    if (!isDefaultValueBlacklisted(value)) {
      return createSummaryValue(value);
    }
  }
  return null;
}

// src/argTypes/docgen/typeScript/createPropDef.ts
var createTsPropDef = (propName, docgenInfo) => {
  const { description, required } = docgenInfo;
  return {
    name: propName,
    type: createType2(docgenInfo),
    required,
    description,
    defaultValue: createDefaultValue2(docgenInfo)
  };
};

// src/argTypes/docgen/createPropDef.ts
function createType3(type) {
  return type != null ? createSummaryValue(type.name) : null;
}
function isReactDocgenTypescript(defaultValue) {
  const { computed, func } = defaultValue;
  return typeof computed === "undefined" && typeof func === "undefined";
}
function isStringValued(type) {
  if (!type) {
    return false;
  }
  if (type.name === "string") {
    return true;
  }
  if (type.name === "enum") {
    return Array.isArray(type.value) && type.value.every(
      ({ value: tv }) => typeof tv === "string" && tv[0] === '"' && tv[tv.length - 1] === '"'
    );
  }
  return false;
}
function createDefaultValue3(defaultValue, type) {
  if (defaultValue != null) {
    const { value } = defaultValue;
    if (!isDefaultValueBlacklisted(value)) {
      if (isReactDocgenTypescript(defaultValue) && isStringValued(type)) {
        return createSummaryValue(JSON.stringify(value));
      }
      return createSummaryValue(value);
    }
  }
  return null;
}
function createBasicPropDef(name, type, docgenInfo) {
  const { description, required, defaultValue } = docgenInfo;
  return {
    name,
    type: createType3(type),
    required,
    description,
    defaultValue: createDefaultValue3(defaultValue, type)
  };
}
function applyJsDocResult(propDef, jsDocParsingResult) {
  if (jsDocParsingResult.includesJsDoc) {
    const { description, extractedTags } = jsDocParsingResult;
    if (description != null) {
      propDef.description = jsDocParsingResult.description;
    }
    const value = {
      ...extractedTags,
      params: extractedTags?.params?.map((x) => ({
        name: x.getPrettyName(),
        description: x.description
      }))
    };
    if (Object.values(value).filter(Boolean).length > 0) {
      propDef.jsDocTags = value;
    }
  }
  return propDef;
}
var javaScriptFactory = (propName, docgenInfo, jsDocParsingResult) => {
  const propDef = createBasicPropDef(propName, docgenInfo.type, docgenInfo);
  propDef.sbType = convert4(docgenInfo);
  return applyJsDocResult(propDef, jsDocParsingResult);
};
var tsFactory = (propName, docgenInfo, jsDocParsingResult) => {
  const propDef = createTsPropDef(propName, docgenInfo);
  propDef.sbType = convert4(docgenInfo);
  return applyJsDocResult(propDef, jsDocParsingResult);
};
var flowFactory = (propName, docgenInfo, jsDocParsingResult) => {
  const propDef = createFlowPropDef(propName, docgenInfo);
  propDef.sbType = convert4(docgenInfo);
  return applyJsDocResult(propDef, jsDocParsingResult);
};
var unknownFactory = (propName, docgenInfo, jsDocParsingResult) => {
  const propDef = createBasicPropDef(propName, { name: "unknown" }, docgenInfo);
  return applyJsDocResult(propDef, jsDocParsingResult);
};
var getPropDefFactory = (typeSystem) => {
  switch (typeSystem) {
    case "JavaScript" /* JAVASCRIPT */:
      return javaScriptFactory;
    case "TypeScript" /* TYPESCRIPT */:
      return tsFactory;
    case "Flow" /* FLOW */:
      return flowFactory;
    default:
      return unknownFactory;
  }
};

// src/argTypes/docgen/extractDocgenProps.ts
var getTypeSystem = (docgenInfo) => {
  if (docgenInfo.type != null) {
    return "JavaScript" /* JAVASCRIPT */;
  }
  if (docgenInfo.flowType != null) {
    return "Flow" /* FLOW */;
  }
  if (docgenInfo.tsType != null) {
    return "TypeScript" /* TYPESCRIPT */;
  }
  return "Unknown" /* UNKNOWN */;
};
var extractComponentSectionArray = (docgenSection) => {
  const typeSystem = getTypeSystem(docgenSection[0]);
  const createPropDef = getPropDefFactory(typeSystem);
  return docgenSection.map((item) => {
    let sanitizedItem = item;
    if (item.type?.elements) {
      sanitizedItem = {
        ...item,
        type: {
          ...item.type,
          value: item.type.elements
        }
      };
    }
    return extractProp(sanitizedItem.name, sanitizedItem, typeSystem, createPropDef);
  });
};
var extractComponentSectionObject = (docgenSection) => {
  const docgenPropsKeys = Object.keys(docgenSection);
  const typeSystem = getTypeSystem(docgenSection[docgenPropsKeys[0]]);
  const createPropDef = getPropDefFactory(typeSystem);
  return docgenPropsKeys.map((propName) => {
    const docgenInfo = docgenSection[propName];
    return docgenInfo != null ? extractProp(propName, docgenInfo, typeSystem, createPropDef) : null;
  }).filter(Boolean);
};
var extractComponentProps = (component, section) => {
  const docgenSection = getDocgenSection(component, section);
  if (!isValidDocgenSection(docgenSection)) {
    return [];
  }
  return Array.isArray(docgenSection) ? extractComponentSectionArray(docgenSection) : extractComponentSectionObject(docgenSection);
};
function extractProp(propName, docgenInfo, typeSystem, createPropDef) {
  const jsDocParsingResult = parseJsDoc(docgenInfo.description);
  const isIgnored = jsDocParsingResult.includesJsDoc && jsDocParsingResult.ignore;
  if (!isIgnored) {
    const propDef = createPropDef(propName, docgenInfo, jsDocParsingResult);
    return {
      propDef,
      jsDocTags: jsDocParsingResult.extractedTags,
      docgenInfo,
      typeSystem
    };
  }
  return null;
}
function extractComponentDescription(component) {
  return component != null && getDocgenDescription(component);
}
var enhanceArgTypes = (context) => {
  const {
    component,
    argTypes: userArgTypes,
    parameters: { docs = {} }
  } = context;
  const { extractArgTypes } = docs;
  const extractedArgTypes = extractArgTypes && component ? extractArgTypes(component) : {};
  const withExtractedTypes = extractedArgTypes ? combineParameters(extractedArgTypes, userArgTypes) : userArgTypes;
  return withExtractedTypes;
};

// src/shared.ts
var ADDON_ID = "storybook/docs";
var PANEL_ID = `${ADDON_ID}/panel`;
var PARAM_KEY = `docs`;
var SNIPPET_RENDERED = `${ADDON_ID}/snippet-rendered`;
var SourceType = /* @__PURE__ */ ((SourceType2) => {
  SourceType2["AUTO"] = "auto";
  SourceType2["CODE"] = "code";
  SourceType2["DYNAMIC"] = "dynamic";
  return SourceType2;
})(SourceType || {});

// src/hasDocsOrControls.ts
var packageRe = /(addons\/|addon-|addon-essentials\/)(docs|controls)/;
var hasDocsOrControls = (options) => options.presetsList?.some((preset) => packageRe.test(preset.name));

export { ADDON_ID, MAX_DEFAULT_VALUE_SUMMARY_LENGTH, MAX_TYPE_SUMMARY_LENGTH, PANEL_ID, PARAM_KEY, SNIPPET_RENDERED, SourceType, TypeSystem, convert4 as convert, createSummaryValue, enhanceArgTypes, extractComponentDescription, extractComponentProps, extractComponentSectionArray, extractComponentSectionObject, getDocgenDescription, getDocgenSection, hasDocgen, hasDocsOrControls, isDefaultValueBlacklisted, isTooLongForDefaultValueSummary, isTooLongForTypeSummary, isValidDocgenSection, normalizeNewlines, parseJsDoc, str };
